import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  ExpandedState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  TableMeta,
  type TableOptions,
  type TableState,
  type Updater,
  type VisibilityState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  type Parser,
  type UseQueryStateOptions,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import * as React from 'react';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { getSortingStateParser } from '@/lib/parsers';

import { DataTableMeta, decodeTableSearch } from '../data-table.utils';
import type { ExtendedColumnSort } from '../types';

const PAGE_KEY = 'page';
const SORT_KEY = 'sort';
const ARRAY_SEPARATOR = ',';
const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;
interface UseDataTableProps<TData, TMeta = unknown>
  extends Omit<
      TableOptions<TData>,
      'state' | 'pageCount' | 'getCoreRowModel' | 'manualFiltering' | 'manualPagination' | 'manualSorting' | 'meta'
    >,
    Required<Pick<TableOptions<TData>, 'pageCount'>> {
  meta?: TMeta;
  initialState?: Omit<Partial<TableState>, 'sorting'> & {
    sorting?: ExtendedColumnSort<TData>[];
  };
  history?: 'push' | 'replace';
  debounceMs?: number;
  throttleMs?: number;
  clearOnDefault?: boolean;
  enableAdvancedFilter?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  startTransition?: React.TransitionStartFunction;
  statusFilterKey?: string;
}

export function useDataTable<TData, TMeta extends TableMeta<TData> | undefined = undefined>(
  props: UseDataTableProps<TData, TMeta>,
) {
  const {
    data,
    columns,
    pageCount = 0,
    initialState,
    meta,
    history = 'replace',
    debounceMs = DEBOUNCE_MS,
    throttleMs = THROTTLE_MS,
    clearOnDefault = false,
    enableAdvancedFilter = false,
    scroll = false,
    shallow = true,
    startTransition,
    statusFilterKey = 'clubStatusFilter',
    ...tableProps
  } = props;

  const queryStateOptions = React.useMemo<Omit<UseQueryStateOptions<string>, 'parse'>>(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    }),
    [history, scroll, shallow, throttleMs, debounceMs, clearOnDefault, startTransition],
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(initialState?.rowSelection ?? {});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialState?.columnVisibility ?? {});

  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  // Read the same committed search state as the data queries. Nuqs updates its
  // local state before the router, which would renumber the previous page's rows.
  const page = typeof search.page === 'number' ? search.page : 0;
  const fetch = typeof search.fetch === 'number' ? search.fetch : (initialState?.pagination?.pageSize ?? 10);
  // Keep filter resets in the same nuqs batch as their corresponding filters.
  const [, setPage] = useQueryState(PAGE_KEY, parseAsInteger.withOptions(queryStateOptions).withDefault(0));

  const pagination: PaginationState = React.useMemo(() => {
    return {
      pageIndex: page,
      pageSize: fetch,
    };
  }, [page, fetch]);

  const isTableLoading = (meta as DataTableMeta<TData> | undefined)?.loading;

  React.useEffect(() => {
    if (pagination.pageIndex === 0 || isTableLoading) return;

    const pageExceedsTotal = pageCount > 0 && pagination.pageIndex >= pageCount;
    const loadedPageIsEmpty = isTableLoading === false && data.length === 0;

    if (pageExceedsTotal || loadedPageIsEmpty) {
      const previousPage = pagination.pageIndex - 1;
      const lastReportedPage = pageCount > 0 ? pageCount - 1 : previousPage;
      void setPage(Math.max(Math.min(previousPage, lastReportedPage), 0));
    }
  }, [data.length, isTableLoading, pageCount, pagination.pageIndex, setPage]);

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue;
      void navigate({
        to: '.',
        search: (previous) => ({ ...previous, page: next.pageIndex, fetch: next.pageSize }),
        replace: history === 'replace',
        resetScroll: scroll,
      });
    },
    [pagination, navigate, history, scroll],
  );

  const resolvedMeta = React.useMemo(
    () =>
      ({
        getSerialNumber: (rowIndex: number) => pagination.pageIndex * pagination.pageSize + rowIndex + 1,
        ...meta,
      }) as DataTableMeta<TData>,
    [meta, pagination.pageIndex, pagination.pageSize],
  );

  const columnIds = React.useMemo(() => {
    return new Set(
      columns
        .map((column) => column.id ?? (column as { accessorKey?: string }).accessorKey)
        .filter(Boolean) as string[],
    );
  }, [columns]);

  const [sorting, setSorting] = useQueryState(
    SORT_KEY,
    getSortingStateParser<TData>(columnIds)
      .withOptions(queryStateOptions)
      .withDefault(initialState?.sorting ?? []),
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(Array.isArray(sorting) ? sorting : []);
        void setSorting(newSorting as ExtendedColumnSort<TData>[]);
      } else {
        void setSorting(updaterOrValue as ExtendedColumnSort<TData>[]);
      }
    },
    [sorting, setSorting],
  );

  const filterableColumns = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return columns.filter((column) => column.enableColumnFilter);
  }, [columns, enableAdvancedFilter]);

  const filterParsers = React.useMemo(() => {
    if (enableAdvancedFilter) return {};

    return filterableColumns.reduce<Record<string, Parser<string> | Parser<string[]>>>((acc, column) => {
      const columnId = column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';

      if (columnId === 'createdDate') {
        acc[columnId] = parseAsArrayOf(parseAsString, ARRAY_SEPARATOR).withOptions(queryStateOptions);
      } else if (column.meta?.options) {
        acc[columnId] = parseAsArrayOf(parseAsString, ARRAY_SEPARATOR).withOptions(queryStateOptions);
      } else {
        acc[columnId] = parseAsString.withOptions(queryStateOptions);
      }

      return acc;
    }, {});
  }, [filterableColumns, queryStateOptions, enableAdvancedFilter]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers);

  // Add this effect to re-sync when URL params hydrate
  React.useEffect(() => {
    setColumnFilters(initialColumnFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const debouncedSetFilterValues = useDebouncedCallback((values: typeof filterValues) => {
    void setPage(0);
    void setFilterValues(values);
  }, debounceMs);

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return Object.entries(filterValues).reduce<ColumnFiltersState>((filters, [key, value]) => {
      if (value !== null) {
        const processedValue = Array.isArray(value) ? value : [value];

        filters.push({
          id: key,
          value: processedValue,
        });
      }
      return filters;
    }, []);
  }, [filterValues, enableAdvancedFilter]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialColumnFilters);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      setColumnFilters((prev) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;

        const filterUpdates = next.reduce<Record<string, string | string[] | null>>((acc, filter) => {
          if (
            filterableColumns.find(
              (column) => (column.id ?? (column as { accessorKey?: string }).accessorKey) === filter.id,
            )
          ) {
            acc[filter.id] = filter.value as string | string[];
          }
          return acc;
        }, {});

        for (const prevFilter of prev) {
          if (!next.some((filter) => filter.id === prevFilter.id)) {
            filterUpdates[prevFilter.id] = null;
          }
        }

        debouncedSetFilterValues(filterUpdates);
        return next;
      });
    },
    [debouncedSetFilterValues, filterableColumns, enableAdvancedFilter],
  );
  const [globalQuery, setGlobalQuery] = useQueryState(
    'q',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );

  const [globalFilter, setGlobalFilter] = React.useState<unknown>(decodeTableSearch(globalQuery ?? ''));

  React.useEffect(() => {
    setGlobalFilter(decodeTableSearch(globalQuery ?? ''));
  }, [globalQuery]);

  const normalizeQueryValue = (value: string) => {
    return value.trim().replace(/\s+/g, ' ');
  };

  const debouncedSetGlobalQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setGlobalQuery(value);
  }, debounceMs);

  const onGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
    debouncedSetGlobalQuery(normalizeQueryValue(value));
  };
  const [expanded, setExpanded] = React.useState<ExpandedState>(initialState?.expanded ?? {});

  const [statusQuery, setStatusQuery] = useQueryState(
    statusFilterKey,
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );

  const [statusFilter, setStatusFilter] = React.useState<unknown>(statusQuery ?? '');
  React.useEffect(() => {
    setStatusFilter(statusQuery ?? '');
  }, [statusQuery]);
  const debouncedSetStatusQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setStatusQuery(value);
  }, debounceMs);
  const onStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    debouncedSetStatusQuery(value);
  };

  const [clubQuery, setClubQuery] = useQueryState(
    'clubId',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );
  const [clubFilter, setClubFilter] = React.useState<unknown>(clubQuery ?? '');
  React.useEffect(() => {
    setClubFilter(clubQuery ?? '');
  }, [clubQuery]);

  const debouncedSetClubQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setClubQuery(value);
  }, debounceMs);
  const onClubFilterChange = (value: string) => {
    setClubFilter(value);
    debouncedSetClubQuery(value);
  };

  const [planQuery, setPlanQuery] = useQueryState(
    'planTypeFilter',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );
  const [planFilter, setPlanFilter] = React.useState<unknown>(planQuery ?? '');
  React.useEffect(() => {
    setPlanFilter(planQuery ?? '');
  }, [planQuery]);
  const debouncedSetPlanQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setPlanQuery(value);
  }, debounceMs);
  const onPlanFilterChange = (value: string) => {
    setPlanFilter(value);
    debouncedSetPlanQuery(value);
  };

  const [ratingQuery, setRatingQuery] = useQueryState(
    'ratingsFilter',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );
  const [ratingsFilter, setRatingsFilter] = React.useState<unknown>(ratingQuery ?? '');
  React.useEffect(() => {
    setRatingsFilter(ratingQuery ?? '');
  }, [ratingQuery]);
  const debouncedSetRatingQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setRatingQuery(value);
  }, debounceMs);
  const onRatingsFilterChange = (value: string) => {
    setRatingsFilter(value);
    debouncedSetRatingQuery(value);
  };

  const [locationQuery, setLocationQuery] = useQueryState(
    'locationFilter',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );
  const [locationFilter, setLocationFilter] = React.useState<unknown>(locationQuery ?? '');
  React.useEffect(() => {
    setLocationFilter(locationQuery ?? '');
  }, [locationQuery]);
  const debouncedSetLocationQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setLocationQuery(value);
  }, debounceMs);
  const onLocationFilterChange = (value: string) => {
    setLocationFilter(value);
    debouncedSetLocationQuery(value);
  };

  const [paymentMethodQuery, setPaymentMethodQuery] = useQueryState(
    'paymentMethodFilter',
    parseAsString.withOptions(queryStateOptions).withDefault(''),
  );
  const [paymentMethodFilter, setPaymentMethodFilter] = React.useState<unknown>(paymentMethodQuery ?? '');
  React.useEffect(() => {
    setPaymentMethodFilter(paymentMethodQuery ?? '');
  }, [paymentMethodQuery]);
  const debouncedSetPaymentMethodQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setPaymentMethodQuery(value);
  }, debounceMs);
  const onPaymentMethodFilterChange = (value: string) => {
    setPaymentMethodFilter(value);
    debouncedSetPaymentMethodQuery(value);
  };

  const [actionTypeQuery, setActionTypeQuery] = useQueryState(
    'actionType',
    parseAsString.withOptions(queryStateOptions).withDefault('all'),
  );
  const [actionTypeFilter, setActionTypeFilter] = React.useState(actionTypeQuery);
  React.useEffect(() => setActionTypeFilter(actionTypeQuery), [actionTypeQuery]);
  const debouncedSetActionTypeQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setActionTypeQuery(value || 'all');
  }, debounceMs);
  const onActionTypeFilterChange = (value: string) => {
    setActionTypeFilter(value || 'all');
    debouncedSetActionTypeQuery(value);
  };

  const [sourcePageTypeQuery, setSourcePageTypeQuery] = useQueryState(
    'sourcePageType',
    parseAsString.withOptions(queryStateOptions).withDefault('all'),
  );
  const [sourcePageTypeFilter, setSourcePageTypeFilter] = React.useState(sourcePageTypeQuery);
  React.useEffect(() => setSourcePageTypeFilter(sourcePageTypeQuery), [sourcePageTypeQuery]);
  const debouncedSetSourcePageTypeQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setSourcePageTypeQuery(value || 'all');
  }, debounceMs);
  const onSourcePageTypeFilterChange = (value: string) => {
    setSourcePageTypeFilter(value || 'all');
    debouncedSetSourcePageTypeQuery(value);
  };

  const [userStatusQuery, setUserStatusQuery] = useQueryState(
    'userStatus',
    parseAsString.withOptions(queryStateOptions).withDefault('all'),
  );
  const [userStatusFilter, setUserStatusFilter] = React.useState(userStatusQuery);
  React.useEffect(() => setUserStatusFilter(userStatusQuery), [userStatusQuery]);
  const debouncedSetUserStatusQuery = useDebouncedCallback((value: string) => {
    void setPage(0);
    void setUserStatusQuery(value || 'all');
  }, debounceMs);
  const onUserStatusFilterChange = (value: string) => {
    setUserStatusFilter(value || 'all');
    debouncedSetUserStatusQuery(value);
  };

  const table = useReactTable({
    ...tableProps,
    data,
    meta: resolvedMeta,
    columns,
    initialState,
    pageCount,
    state: {
      expanded,
      pagination,
      sorting: Array.isArray(sorting) ? sorting : [],
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false,
    },
    onExpandedChange: setExpanded,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onGlobalFilterChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  // table.setStatusFilter = onStatusFilterChange;
  // table.setClubFilter = onClubFilterChange;
  // table.setPlanFilter = onPlanFilterChange;
  return {
    table,
    shallow,
    debounceMs,
    throttleMs,
    statusFilter,
    clubFilter,
    planFilter,
    paymentMethodFilter,
    ratingsFilter,
    locationFilter,
    actionTypeFilter,
    sourcePageTypeFilter,
    userStatusFilter,
    onStatusFilterChange,
    onClubFilterChange,
    onPlanFilterChange,
    onPaymentMethodFilterChange,
    onRatingsFilterChange,
    onLocationFilterChange,
    onActionTypeFilterChange,
    onSourcePageTypeFilterChange,
    onUserStatusFilterChange,
  };
}
